import { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cva } from 'class-variance-authority';
import { cn } from '../utils/cn';

interface TabItem {
  path: string;
  label: string;
  icon?: React.ReactNode;
}

interface TabMenuProps {
  tabs: TabItem[];
  basePath: string;
  className?: string;
}

// Below lg the tabs are a single non-wrapping strip that scrolls sideways —
// wrapping them produced two ragged rows on both a phone and an 820px tablet
// (whose content column is ~590px once the sidebar is open). From lg up the
// sizing is unchanged from the original desktop treatment.
const tabVariants = cva(
  'cr-flex cr-shrink-0 cr-items-center cr-justify-center cr-gap-x-1.5 cr-whitespace-nowrap cr-rounded-md '
  + 'cr-px-3 cr-py-2 cr-text-sm cr-font-semibold cr-font-main cr-no-underline cr-shadow-md '
  + 'cr-transition-colors cr-duration-200 '
  + 'lg:cr-gap-x-2 lg:cr-px-4 lg:cr-py-2.5 lg:cr-text-base',
  {
    variants: {
      active: {
        true: 'cr-bg-blue-900 cr-text-white',
        false: 'cr-bg-cirrusBlue-100 cr-text-blue-900 hover:cr-bg-gray-350',
      },
    },
    defaultVariants: {
      active: false,
    },
  }
);

export { tabVariants };

export default function TabMenu({ tabs, basePath, className = '' }: TabMenuProps): ReactElement {
  const location = useLocation();
  const currentPath = location.pathname;

  return (
    <div className={cn('cr-w-full', className)}>
      <nav
        className={
          'cr-flex cr-gap-2 cr-overflow-x-auto cr-pb-1 '
          + 'lg:cr-flex-wrap lg:cr-overflow-x-visible lg:cr-pb-0'
        }
        aria-label="Tabs"
      >
        {tabs.map((tab) => {
          const tabPath = `${basePath}/${tab.path}`;
          const isActive = currentPath === tabPath || (tab.path === '' && currentPath === basePath);

          return (
            <Link
              key={tab.path}
              to={tabPath}
              className={tabVariants({ active: isActive })}
            >
              {tab.label}
              {tab.icon && <span className="cr-m-0">{tab.icon}</span>}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
